/**
 * @fileoverview Connect active-matter contact-age evidence to staff review and explicitly selected reminder sends. Backend rules determine qualifying communications and eligibility; `MatterContactReminderFilters.js` owns controlled filtering. Reading an overdue matter and delivering a reminder are separate actions with separate recorded outcomes.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../../api';
import ToastContext from '../../ToastContext';
import { AppButton, AppSpinner, AppTag } from '../shared';
import { AppDataTable, AppColumn } from '../shared/AppDataTable';
import MatterContactReminderFilters from './MatterContactReminderFilters';

const STATUS_SEVERITY = {
    due_now: 'warning',
    reminded_recently: 'info',
    baseline_suppressed: 'secondary',
    baseline_pending_first_run: 'secondary',
    missing_recipient: 'danger',
};

const RECIPIENT_SEVERITY = {
    mapped_attorney: 'success',
    admin_fallback: 'warning',
    needs_slack_lookup: 'info',
    missing_recipient: 'danger',
};

const LITIGATION_STAGES = new Set(['State Court', 'Federal Court']);



const getStageColor = (stage) => {
    switch (stage) {
    case 'Pre-Demand':
        return 'yellow';
    case 'Demand Sent':
        return 'cyan';
    case 'Arbitration-AAA':
        return 'primary';
    case 'Arbitration-JAMS':
        return 'blue';
    case 'Arbitration-Other':
        return 'purple';
    case 'State Court':
        return 'red';
    case 'Federal Court':
        return 'orange';
    case 'Appeal':
        return 'teal';
    case 'Post Judgment':
        return 'indigo';
    case 'Settled/Resolved':
        return 'green';
    case 'Withdrawn':
        return 'bluegray';
    default:
        return 'gray';
    }
};

const TAG_STYLE_BY_COLOR = {
    yellow: { backgroundColor: '#eab308', color: '#111827', borderColor: '#ca8a04' },
    cyan: { backgroundColor: '#06b6d4', color: '#ffffff', borderColor: '#0891b2' },
    primary: { backgroundColor: 'var(--primary-500, #3b82f6)', color: '#ffffff', borderColor: 'var(--primary-600, #2563eb)' },
    blue: { backgroundColor: '#3b82f6', color: '#ffffff', borderColor: '#2563eb' },
    purple: { backgroundColor: '#a855f7', color: '#ffffff', borderColor: '#9333ea' },
    red: { backgroundColor: '#ef4444', color: '#ffffff', borderColor: '#dc2626' },
    orange: { backgroundColor: '#f97316', color: '#ffffff', borderColor: '#ea580c' },
    teal: { backgroundColor: '#14b8a6', color: '#ffffff', borderColor: '#0f766e' },
    indigo: { backgroundColor: '#6366f1', color: '#ffffff', borderColor: '#4f46e5' },
    green: { backgroundColor: '#22c55e', color: '#ffffff', borderColor: '#16a34a' },
    bluegray: { backgroundColor: '#64748b', color: '#ffffff', borderColor: '#475569' },
    gray: { backgroundColor: '#6b7280', color: '#ffffff', borderColor: '#4b5563' },
};



const getTagStyle = (color) => TAG_STYLE_BY_COLOR[color] || TAG_STYLE_BY_COLOR.gray;

const DEFAULT_FILTERS = {
    search: '',
    status: [],
    stage: [],
    attorney: [],
    recipient: [],
};



const normalizeFilterValue = (value) => (value || '').toString().trim().toLowerCase();



function MatterContactReminders() {
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sendingMatterNumber, setSendingMatterNumber] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const showToast = useContext(ToastContext);

    const fetchDashboard = useCallback(

    async () => {
        setLoading(true);
        try {
            const response = await api.get('/automations/matter-contact-reminders/offenders');
            setDashboard(response.data);
            setLastRefresh(new Date());
        } catch (error) {
            console.error('Error fetching matter contact reminder dashboard:', error);
            showToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load matter contact reminders',
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    const handleSendNow = useCallback(

    async (matterNumber) => {
        setSendingMatterNumber(matterNumber);
        try {
            const response = await api.post(`/automations/matter-contact-reminders/${encodeURIComponent(matterNumber)}/send-now`);
            const result = response.data?.result || {};

            showToast({
                severity: 'success',
                summary: 'Matter Contact Reminder Sent',
                detail: response.data?.message
                    || `Reminder sent for ${result.matter_number || matterNumber}.`,
                life: 4000,
            });
            await fetchDashboard();
        } catch (error) {
            console.error('Error sending matter contact reminder now:', error);
            showToast({
                severity: 'error',
                summary: 'Error',
                detail: error?.response?.data?.error || `Failed to send reminder for ${matterNumber}`,
                life: 4000,
            });
        } finally {
            setSendingMatterNumber(null);
        }
    }, [fetchDashboard, showToast]);

    const formatDateTime =

    (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleString();
    };

    const statusBody =

    (row) => (
        <AppTag
            value={row.status.replaceAll('_', ' ')}
            severity={STATUS_SEVERITY[row.status] || 'info'}
        />
    );

    const recipientBody =

    (row) => (
        <div className="flex flex-column gap-1">
            <AppTag
                value={row.recipient_status.replaceAll('_', ' ')}
                severity={RECIPIENT_SEVERITY[row.recipient_status] || 'info'}
            />
            <span className="text-sm text-600">
                {row.notification_target_preview || row.recipient_reason || '-'}
            </span>
        </div>
    );

    const matterBody =

    (row) => {
        if (!row.matter_number) {
            return '-';
        }

        return (
            <a
                href={`/case_details/${encodeURIComponent(row.matter_number)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary no-underline hover:underline"
            >
                {row.matter_number}
            </a>
        );
    };

    const stageBody =

    (row) => (
        <div className="flex flex-column gap-1">
            <AppTag
                value={row.stage_name || row.stage_id || '-'}
                className="w-fit"
                style={getTagStyle(getStageColor(row.stage_name))}
            />
            {LITIGATION_STAGES.has(row.stage_name) ? (
                <AppTag
                    value={row.substage_name || row.substage_id || 'EMPTY'}
                    className="w-fit"
                    style={getTagStyle(row.substage_name || row.substage_id ? 'red' : 'gray')}
                />
            ) : row.stage_name === 'Settled/Resolved' ? (
                <AppTag
                    value={row.substage_name || row.substage_id || 'N/A'}
                    className="w-fit"
                    style={getTagStyle('green')}
                />
            ) : (
                <AppTag
                    value="N/A"
                    className="w-fit"
                    style={getTagStyle('gray')}
                />
            )}
        </div>
    );

    const basisBody =

    (row) => (
        <div className="flex flex-column gap-1">
            <span>{row.basis_source === 'activity' ? (row.basis_activity_type || 'Outbound activity') : 'Date engaged'}</span>
            <span className="text-sm text-600">{formatDateTime(row.basis_at)}</span>
        </div>
    );

    const dateBody =

    (row, col) => formatDateTime(row[col.field]);

    const actionBody =

    (row) => {
        const missingRecipient = row.recipient_status === 'missing_recipient';
        const isSending = sendingMatterNumber === row.matter_number;

        return (
            <AppButton
                label={isSending ? 'Sending...' : 'Send Now'}
                icon={isSending ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
                onClick={() => handleSendNow(row.matter_number)}
                disabled={loading || isSending || missingRecipient}
                style={{ whiteSpace: 'nowrap' }}
            />
        );
    };

    const summary = dashboard?.summary || {};
    const offenders = dashboard?.offenders || [];
    const stageOptions = useMemo(
        () => [...new Set(offenders.map((row) => row.stage_name).filter(Boolean))].sort(),
        [offenders],
    );
    const statusOptions = useMemo(
        () => [...new Set(offenders.map((row) => row.status).filter(Boolean))].sort(),
        [offenders],
    );
    const attorneyOptions = useMemo(
        () => [...new Set(offenders.map((row) => row.attorney_name).filter(Boolean))].sort(),
        [offenders],
    );
    const recipientOptions = useMemo(
        () => [...new Set(offenders.map((row) => row.recipient_status).filter(Boolean))].sort(),
        [offenders],
    );

    const filteredOffenders = useMemo(() => {
        const search = normalizeFilterValue(filters.search);

        return offenders.filter(

        (row) => {
            const matchesSearch = !search || [
                row.matter_number,
                row.primary_client_name,
                row.attorney_name,
                row.stage_name,
                row.substage_name,
                row.notification_target_preview,
                row.recipient_reason,
            ].some((value) => normalizeFilterValue(value).includes(search));

            const matchesStatus = filters.status.length === 0 || filters.status.includes(row.status);
            const matchesStage = filters.stage.length === 0 || filters.stage.includes(row.stage_name);
            const matchesAttorney = filters.attorney.length === 0 || filters.attorney.includes(row.attorney_name);
            const matchesRecipient = filters.recipient.length === 0 || filters.recipient.includes(row.recipient_status);

            return matchesSearch && matchesStatus && matchesStage && matchesAttorney && matchesRecipient;
        });
    }, [filters.attorney, filters.recipient, filters.search, filters.stage, filters.status, offenders]);

    const handleFilterChange = useCallback(

    (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    }, []);

    const handleClearFilters = useCallback(

    () => {
        setFilters(DEFAULT_FILTERS);
    }, []);

    const summaryCards = [
        { label: 'Offending Matters', value: summary.total_offenders || 0 },
        { label: 'Sendable', value: summary.sendable || 0 },
        { label: 'Baseline Suppressed', value: summary.baseline_suppressed || 0 },
        { label: 'Needs Slack Lookup', value: summary.needs_slack_lookup || 0 },
        { label: 'Missing Recipient', value: summary.missing_recipient || 0 },
        { label: 'Invalid / Skipped', value: summary.skipped_invalid || 0 },
    ];

    return (
        <div className="matter-contact-reminders">
            <div className="surface-card p-4 mb-3 border-round">
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-start gap-3">
                    <div>
                        <div className="text-900 text-2xl font-medium mb-2">Matter Contact Reminders</div>
                        <div className="text-600">
                            Active, non-hidden matters whose latest qualifying outbound communication is older than 30 days.
                        </div>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <AppButton
                            label="Refresh"
                            icon="pi pi-refresh"
                            onClick={fetchDashboard}
                            disabled={loading || Boolean(sendingMatterNumber)}
                        />
                    </div>
                </div>
                <div className="grid mt-3">
                    {summaryCards.map((card) => (
                        <div key={card.label} className="col-12 md:col-4 xl:col-2">
                            <div className="surface-ground border-round p-3 h-full">
                                <div className="text-600 text-sm mb-2">{card.label}</div>
                                <div className="text-900 text-2xl font-semibold">{card.value}</div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="text-sm text-600 mt-2">
                    Last refreshed: {lastRefresh.toLocaleTimeString()}
                    {dashboard?.as_of ? ` · Data as of ${new Date(dashboard.as_of).toLocaleString()}` : ''}
                </div>
            </div>

            <MatterContactReminderFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearAll={handleClearFilters}
                filteredCount={filteredOffenders.length}
                totalCount={offenders.length}
                stageOptions={stageOptions}
                statusOptions={statusOptions}
                attorneyOptions={attorneyOptions}
                recipientOptions={recipientOptions}
                isLoading={loading || Boolean(sendingMatterNumber)}
            />

            {loading && !dashboard ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '240px' }}>
                    <AppSpinner />
                </div>
            ) : (
                <div className="surface-card p-4 border-round">
                    <AppDataTable
                        value={filteredOffenders}
                        loading={loading}
                        stripedRows
                        paginator
                        rows={25}
                        responsiveLayout="scroll"
                        sortField="days_stale"
                        sortOrder={-1}
                        emptyMessage="No offending matters."
                    >
                        <AppColumn field="status" header="Status" body={statusBody} sortable />
                        <AppColumn field="matter_number" header="Matter" body={matterBody} sortable />
                        <AppColumn field="primary_client_name" header="Client" sortable />
                        <AppColumn field="attorney_name" header="Attorney" sortable />
                        <AppColumn header="Stage" body={stageBody} sortable sortField="stage_name" />
                        <AppColumn header="Basis" body={basisBody} sortable sortField="basis_at" />
                        <AppColumn field="due_at" header="Due" body={dateBody} sortable />
                        <AppColumn field="days_stale" header="Days Stale" sortable />
                        <AppColumn header="Recipient" body={recipientBody} sortable sortField="recipient_status" />
                        <AppColumn field="last_reminder_sent_at" header="Last Sent" body={dateBody} sortable />
                        <AppColumn header="Actions" body={actionBody} />
                    </AppDataTable>
                </div>
            )}
        </div>
    );
}

export default MatterContactReminders;
